import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp, Home } from "lucide-react";
import type { ListingOverview } from "@/types/listing";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/context/LanguageContext";
import { auctionsLabels } from "@/i18n/labels/auctionsLabels";
import { commonLabels } from "@/i18n/labels/commonLabels";
import { cn } from "@/lib/utils";
import {
  AuctionsBrowseTabs,
  type AuctionsBrowseMode,
} from "@/components/auctions/AuctionsBrowseTabs";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import {
  AuctionsGuestHeaderHome,
  AuctionsGuestHeaderLogin,
} from "@/components/auctions/AuctionsGuestHeaderNav";
import { ForYouCard } from "./ForYouCard";
import { ForYouListingSheet } from "./ForYouListingSheet";
import { ForYouFeedSkeleton } from "./ForYouFeedSkeleton";
import { isGalleryFullscreenOpen } from "@/lib/galleryFullscreenLock";
import { useForYouTheme } from "./forYouTheme";
import { useBrowseModeSwipe } from "./useBrowseModeSwipe";

const FOR_YOU_RESUME_PREFIX = "forYou_lastListingId:";
/** Prefetch next page when this many cards remain. */
const LOAD_MORE_THRESHOLD = 3;

type ForYouFeedProps = {
  listings: ListingOverview[];
  onFavoriteToggle?: (listingId: string) => void;
  browseMode: AuctionsBrowseMode;
  onBrowseModeChange: (mode: AuctionsBrowseMode) => void;
  /** Scope for resume position (e.g. "all" or campaignId) */
  resumeScope?: string;
  className?: string;
  /**
   * Gallery scrub while entering from Alla (0 = off-screen right, 1 = filled).
   * When set, shell follows this transform 1:1 (no fade placeholder).
   */
  dragProgress?: number;
  /** True while parent is settling the swipe commit / release */
  dragSettling?: boolean;
  /** Preview during swipe — no interaction, no body scroll lock */
  preview?: boolean;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  /** Pull-down on first card refreshes the feed */
  onRefresh?: () => void | Promise<void>;
};

const PULL_ARM_PX = 12;
const PULL_THRESHOLD_PX = 70;
const PULL_MAX_PX = 118;

export function ForYouFeed({
  listings,
  onFavoriteToggle,
  browseMode,
  onBrowseModeChange,
  resumeScope = "all",
  className,
  dragProgress,
  dragSettling = false,
  preview = false,
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
  onRefresh,
}: ForYouFeedProps) {
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const { user } = useAuth();
  const isGuest = !user;
  const { language } = useLanguage();
  const labels = auctionsLabels(language);
  const fy = labels.forYou;
  const t = useForYouTheme();
  const feedRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const detailListingIdRef = useRef<string | null>(null);
  /** Ignore Escape→exit-För-dig briefly after sheet closes (same keypress). */
  const suppressExitUntilRef = useRef(0);
  const restoredRef = useRef(false);
  const loadMoreRequestedRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [atEndPage, setAtEndPage] = useState(false);
  const [detailListingId, setDetailListingId] = useState<string | null>(null);
  const [pullPx, setPullPx] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const layout = isMobile || isNative ? "mobile" : "desktop";
  detailListingIdRef.current = detailListingId;
  const resumeKey = `${FOR_YOU_RESUME_PREFIX}${resumeScope}`;
  const activeIndexRef = useRef(0);
  activeIndexRef.current = activeIndex;
  const pullPxRef = useRef(0);
  pullPxRef.current = pullPx;
  const refreshingRef = useRef(false);
  refreshingRef.current = refreshing;
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const [exitProgress, setExitProgress] = useState(0); // 0..1 swiping back to Alla
  const [exiting, setExiting] = useState(false);
  const exitingRef = useRef(false);

  const leaveToAll = useCallback(() => {
    if (exitingRef.current) return;
    exitingRef.current = true;
    setExiting(true);
    setExitProgress(1);
    window.setTimeout(() => {
      onBrowseModeChange("all");
    }, 280);
  }, [onBrowseModeChange]);

  // Mobile: swipe back to Alla from left edge (gallery also handles i=0 + info)
  useBrowseModeSwipe(shellRef, {
    enabled: layout === "mobile" && !detailListingId && !exiting && !preview,
    onSwipeRight: leaveToAll,
    onProgress: (p) => {
      setExitProgress(Math.max(0, -p));
    },
    onCancel: () => {
      if (!exitingRef.current) setExitProgress(0);
    },
    thresholdPx: 56,
    edge: "left",
    edgePx: 28,
  });

  // Already popularity-sorted by the API; keep order stable across pages.
  const sorted = listings;
  const showEndSentinel = !hasMore && !isFetchingMore && sorted.length > 0;

  const cardLabels = useMemo(
    () => ({
      directPurchase: labels.listing.type["direct-purchase"],
      auction: labels.listing.type.auction,
      currentBid: fy.currentBid,
      pressForFullAd: fy.pressForFullAd,
      viewFullAd: fy.viewFullAd,
      goToListing: fy.goToListing,
      goingToListing: fy.goingToListing,
      addFavorite: labels.carCard.addFavorite,
      removeFavorite: labels.carCard.removeFavorite,
      share: fy.share,
      shared: fy.shared,
      shareFailed: fy.shareFailed,
      accidentFree: fy.accidentFree,
      oneOwner: fy.oneOwner,
      owners: fy.owners,
      inspected: fy.inspected,
      testDriven: fy.testDriven,
      year: fy.year,
      mileage: fy.mileage,
      range: fy.range,
      transmission: fy.transmission,
      fuel: fy.fuel,
      annualTax: fy.annualTax,
      moreEquipment: fy.moreEquipment,
      daysLeft: fy.daysLeft,
      hoursLeft: fy.hoursLeft,
      endingSoon: fy.endingSoon,
      currencyShort: labels.currencyShort,
    }),
    [labels, fy],
  );

  // Lock page scroll while immersive feed is open (not during swipe preview)
  useEffect(() => {
    if (preview) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [preview]);

  const syncActive = useCallback(() => {
    const el = feedRef.current;
    if (!el) return;
    const h = el.clientHeight || 1;
    const page = Math.round(el.scrollTop / h);
    const lastListing = Math.max(0, sorted.length - 1);
    if (showEndSentinel && page >= sorted.length) {
      setAtEndPage(true);
      setActiveIndex(lastListing);
    } else {
      setAtEndPage(false);
      setActiveIndex(Math.max(0, Math.min(lastListing, page)));
    }
  }, [sorted.length, showEndSentinel]);

  useEffect(() => {
    const el = feedRef.current;
    if (!el) return;
    el.addEventListener("scroll", syncActive, { passive: true });
    return () => el.removeEventListener("scroll", syncActive);
  }, [syncActive]);

  // Pull-to-refresh on first card (capture so gallery vertical JS doesn't swallow it)
  useEffect(() => {
    const feed = feedRef.current;
    if (!feed || preview || !onRefresh) return;

    let startY = 0;
    let armed = false;
    let pulling = false;

    const onStart = (e: TouchEvent) => {
      if (detailListingIdRef.current || refreshingRef.current) return;
      if (activeIndexRef.current !== 0) return;
      if (feed.scrollTop > 4) return;
      startY = e.touches[0].clientY;
      armed = true;
      pulling = false;
    };

    const onMove = (e: TouchEvent) => {
      if (!armed || refreshingRef.current) return;
      const dy = e.touches[0].clientY - startY;
      if (dy > PULL_ARM_PX && feed.scrollTop <= 4 && activeIndexRef.current === 0) {
        pulling = true;
        e.preventDefault();
        e.stopPropagation();
        const next = Math.min(PULL_MAX_PX, (dy - PULL_ARM_PX) * 0.42);
        pullPxRef.current = next;
        setPullPx(next);
      } else if (!pulling && dy < -8) {
        armed = false;
        pullPxRef.current = 0;
        setPullPx(0);
      }
    };

    const onEnd = () => {
      if (!pulling) {
        armed = false;
        return;
      }
      const distance = pullPxRef.current;
      armed = false;
      pulling = false;
      if (distance >= PULL_THRESHOLD_PX && onRefreshRef.current) {
        setRefreshing(true);
        setPullPx(56);
        void Promise.resolve(onRefreshRef.current())
          .catch(() => undefined)
          .finally(() => {
            setRefreshing(false);
            pullPxRef.current = 0;
            setPullPx(0);
          });
      } else {
        pullPxRef.current = 0;
        setPullPx(0);
      }
    };

    feed.addEventListener("touchstart", onStart, { passive: true, capture: true });
    feed.addEventListener("touchmove", onMove, { passive: false, capture: true });
    feed.addEventListener("touchend", onEnd, { capture: true });
    feed.addEventListener("touchcancel", onEnd, { capture: true });
    return () => {
      feed.removeEventListener("touchstart", onStart, true);
      feed.removeEventListener("touchmove", onMove, true);
      feed.removeEventListener("touchend", onEnd, true);
      feed.removeEventListener("touchcancel", onEnd, true);
    };
  }, [preview, onRefresh, layout]);

  // Resume last listing once feed is mounted (listing id — survives re-sort)
  useEffect(() => {
    if (restoredRef.current || sorted.length === 0) return;
    let cancelled = false;
    let frames = 0;
    const tryRestore = () => {
      if (cancelled || restoredRef.current) return;
      const el = feedRef.current;
      if (!el || el.clientHeight < 8) {
        if (frames++ < 30) requestAnimationFrame(tryRestore);
        else restoredRef.current = true;
        return;
      }
      restoredRef.current = true;
      try {
        const savedId = localStorage.getItem(resumeKey);
        if (!savedId) return;
        const idx = sorted.findIndex((l) => l.id === savedId);
        if (idx <= 0) return;
        el.scrollTop = idx * el.clientHeight;
        setActiveIndex(idx);
      } catch {
        // private mode / storage blocked
      }
    };
    tryRestore();
    return () => {
      cancelled = true;
    };
  }, [sorted, resumeKey]);

  // Persist current listing between sessions
  useEffect(() => {
    if (!restoredRef.current) return;
    const listing = sorted[activeIndex];
    if (!listing) return;
    try {
      localStorage.setItem(resumeKey, listing.id);
    } catch {
      // ignore
    }
  }, [activeIndex, sorted, resumeKey]);

  // Prefetch next page when approaching the end of the loaded feed
  useEffect(() => {
    if (preview || !hasMore || !onLoadMore) return;
    if (sorted.length === 0) return;
    const nearEnd = activeIndex >= sorted.length - LOAD_MORE_THRESHOLD;
    if (!nearEnd) {
      loadMoreRequestedRef.current = false;
      return;
    }
    if (isFetchingMore || loadMoreRequestedRef.current) return;
    loadMoreRequestedRef.current = true;
    onLoadMore();
  }, [
    activeIndex,
    sorted.length,
    hasMore,
    isFetchingMore,
    onLoadMore,
    preview,
  ]);

  useEffect(() => {
    if (!isFetchingMore) loadMoreRequestedRef.current = false;
  }, [isFetchingMore]);

  const scrollByPage = (dir: -1 | 1) => {
    const el = feedRef.current;
    if (!el) return;
    el.scrollBy({ top: dir * el.clientHeight, behavior: "smooth" });
  };

  useEffect(() => {
    if (layout !== "desktop") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        if (detailListingIdRef.current) return;
        e.preventDefault();
        scrollByPage(1);
      } else if (e.key === "ArrowUp") {
        if (detailListingIdRef.current) return;
        e.preventDefault();
        scrollByPage(-1);
      } else if (e.key === "Escape") {
        // Layered dismiss: gallery → sheet → För dig. Never skip a layer.
        if (
          isGalleryFullscreenOpen() ||
          document.querySelector("[data-bilenia-gallery-fullscreen]")
        ) {
          return;
        }
        // Sheet open, or just closed by this same Escape — stay in För dig
        if (
          detailListingIdRef.current ||
          Date.now() < suppressExitUntilRef.current ||
          document.querySelector('[role="dialog"][data-state="open"]')
        ) {
          return;
        }
        e.preventDefault();
        leaveToAll();
      }
    };
    // Bubble phase so Radix (document capture) closes the sheet first
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [layout, leaveToAll]);

  const scrubbing = dragProgress != null && dragProgress >= 0;
  const scrubX = scrubbing ? (1 - (dragProgress ?? 0)) * 100 : null;
  const exitX = exitProgress > 0.001 ? exitProgress * 100 : null;
  const shellX = scrubX != null ? scrubX : exitX != null ? exitX : 0;
  const shellAnimating = dragSettling || exiting;
  // Skip framer enter when parent is gallery-scrubbing the feed in
  const skipEnterAnim = scrubbing || preview;

  const shell = (
    <div
      ref={shellRef}
      className={cn(
        // Above header (50) and native bottom nav (80); sheets stay at 100+
        "fixed inset-0 z-[90] flex flex-col",
        t.shell,
        className,
      )}
      style={{
        paddingBottom: isNative
          ? "env(safe-area-inset-bottom, 0px)"
          : undefined,
        transform: `translate3d(${shellX}%, 0, 0)`,
        opacity:
          exitProgress > 0.001
            ? Math.max(0.2, 1 - exitProgress * 0.75)
            : skipEnterAnim && !scrubbing
              ? 1
              : 1,
        transition: shellAnimating
          ? "transform 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 260ms ease"
          : scrubbing || exitProgress > 0.001
            ? "none"
            : skipEnterAnim
              ? "none"
              : "transform 400ms cubic-bezier(0.22, 1, 0.36, 1), opacity 360ms ease",
        willChange: "transform",
        pointerEvents: preview ? "none" : undefined,
      }}
    >
      {/* Full-width top fade — always dark so onDark tabs stay readable in light mode */}
      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 z-40",
          layout === "desktop" ? "h-28" : "h-44",
        )}
        style={{ background: t.shellFade }}
      />
      <div
        className="absolute inset-x-0 top-0 z-40 grid grid-cols-[minmax(2.5rem,auto)_1fr_minmax(2.5rem,auto)] items-center gap-1 px-3 pb-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}
      >
        <div className="pointer-events-auto relative z-20 justify-self-start">
          {!isGuest ? (
            <BtnNavigateBack
              label={commonLabels(language).menu?.dashboard ?? "Dashboard"}
              to="/dashboard"
              iconOnly={layout === "mobile" || isNative}
              icon={layout === "mobile" || isNative ? Home : undefined}
              className={cn(
                "mb-0 h-9 rounded-xl text-sm font-medium border-0",
                layout === "mobile" || isNative
                  ? "w-9 px-0 justify-center"
                  : "px-2.5",
                t.dashBtn,
              )}
            />
          ) : (
            <AuctionsGuestHeaderHome
              compact={layout === "mobile" || isNative}
              buttonClassName={t.dashBtn}
            />
          )}
        </div>
        <AuctionsBrowseTabs
          mode={browseMode}
          onModeChange={(mode) => {
            if (mode === "all") leaveToAll();
            else onBrowseModeChange(mode);
          }}
          allLabel={labels.browse.allCars}
          forYouLabel={labels.browse.forYou}
          variant="onDark"
          className="pointer-events-auto"
        />
        <div className="pointer-events-auto relative z-20 justify-self-end">
          {isGuest ? (
            <AuctionsGuestHeaderLogin
              compact={layout === "mobile" || isNative}
              buttonClassName={t.dashBtn}
            />
          ) : (
            <span className="invisible pointer-events-none" aria-hidden>
              {layout === "mobile" || isNative ? (
                <span className="inline-flex h-9 w-9" />
              ) : (
                <span className="inline-flex h-9 items-center px-2.5 text-sm font-medium">
                  {commonLabels(language).menu?.dashboard ?? "Dashboard"}
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      {sorted.length === 0 ? (
        <div
          className={cn(
            "flex flex-1 items-center justify-center",
            t.empty,
          )}
        >
          {fy.empty}
        </div>
      ) : (
        <div className="relative min-h-0 flex-1">
          {(pullPx > 0 || refreshing) && (
            <div
              className="pointer-events-none absolute inset-x-0 z-50 flex flex-col items-center"
              style={{
                top: "calc(env(safe-area-inset-top, 0px) + 4.25rem)",
                transform: `translate3d(0, ${Math.max(0, pullPx - 28)}px, 0)`,
                opacity: refreshing
                  ? 1
                  : Math.min(1, pullPx / PULL_THRESHOLD_PX),
              }}
              aria-live="polite"
            >
              <div
                className={cn(
                  "rounded-full px-3.5 py-1.5 text-[12px] font-medium backdrop-blur-md ring-1",
                  t.feedNav,
                )}
              >
                {refreshing
                  ? fy.refreshing
                  : pullPx >= PULL_THRESHOLD_PX
                    ? fy.releaseToRefresh
                    : fy.pullToRefresh}
              </div>
            </div>
          )}
          <div
            ref={feedRef}
            data-foryou-feed-scroll=""
            className="h-full touch-pan-y snap-y snap-mandatory overflow-y-auto overscroll-y-none scrollbar-none"
            style={{
              scrollbarWidth: "none",
              WebkitOverflowScrolling: "touch",
              transform:
                pullPx > 0 || refreshing
                  ? `translate3d(0, ${refreshing ? 36 : pullPx * 0.55}px, 0)`
                  : undefined,
              transition:
                pullPx === 0 && !refreshing
                  ? "transform 280ms cubic-bezier(0.22, 0.82, 0.28, 1)"
                  : "none",
            }}
          >
            {sorted.map((listing, index) => (
              <div
                key={listing.id}
                className="h-full w-full shrink-0 snap-start"
              >
                <ForYouCard
                  listing={listing}
                  labels={cardLabels}
                  layout={layout}
                  isActive={Math.abs(index - activeIndex) <= 1 && !atEndPage}
                  onOpenDetail={() => setDetailListingId(listing.id)}
                  onFavoriteToggle={onFavoriteToggle}
                  detailOpen={detailListingId === listing.id}
                  onBackToAll={layout === "mobile" ? leaveToAll : undefined}
                />
              </div>
            ))}
            {isFetchingMore && (
              <div
                className={cn(
                  "flex h-16 w-full shrink-0 items-center justify-center text-sm",
                  t.empty,
                )}
                aria-hidden
              >
                …
              </div>
            )}
            {showEndSentinel && (
              <div
                className="flex h-[42%] w-full shrink-0 snap-start flex-col items-center justify-start px-6 pt-10"
                aria-label={fy.reachedEnd}
              >
                <p
                  className={cn(
                    "text-center text-[15px] font-semibold tracking-tight",
                    t.title,
                  )}
                >
                  {fy.reachedEnd}
                </p>
                <p
                  className={cn(
                    "mt-1.5 max-w-[16rem] text-center text-[12.5px] leading-snug",
                    t.subtitle,
                  )}
                >
                  {fy.reachedEndHint}
                </p>
              </div>
            )}
          </div>

          {layout === "desktop" && !detailListingId && (
            // Inside the info column (right island), inset from its right edge
            <div className="pointer-events-none absolute inset-y-0 right-[calc(1.25rem+0.75rem)] z-30 flex items-center xl:right-[calc(2rem+0.75rem)]">
              <div className="pointer-events-auto flex flex-col gap-2">
                <button
                  type="button"
                  aria-label={fy.prevCar}
                  disabled={activeIndex <= 0}
                  onClick={() => scrollByPage(-1)}
                  className={cn(
                    "grid h-11 w-8 place-items-center rounded-xl border-0 ring-1 backdrop-blur-md outline-none focus:outline-none focus-visible:ring-sky-400/40 disabled:opacity-35",
                    t.feedNav,
                  )}
                >
                  <ChevronUp className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  aria-label={fy.nextCar}
                  disabled={
                    activeIndex >= sorted.length - 1 && !hasMore && !isFetchingMore
                  }
                  onClick={() => {
                    if (activeIndex >= sorted.length - 1) {
                      onLoadMore?.();
                      return;
                    }
                    scrollByPage(1);
                  }}
                  className={cn(
                    "grid h-11 w-8 place-items-center rounded-xl border-0 ring-1 backdrop-blur-md outline-none focus:outline-none focus-visible:ring-sky-400/40 disabled:opacity-35",
                    t.feedNav,
                  )}
                >
                  <ChevronDown className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );

  const listingSheet = (
    <ForYouListingSheet
      open={!!detailListingId}
      listingId={detailListingId}
      onOpenChange={(open) => {
        if (!open) {
          // Prevent the same Escape that closed the sheet from also exiting För dig
          suppressExitUntilRef.current = Date.now() + 500;
          detailListingIdRef.current = null;
          setDetailListingId(null);
        }
      }}
    />
  );

  if (typeof document === "undefined") return null;
  // Sheet outside the z-90 shell so it never stacks behind the feed
  return (
    <>
      {createPortal(shell, document.body)}
      {listingSheet}
    </>
  );
}
